const STORAGE_PREFIX = 'ipe:recharge-cart:v1';
const CART_CHANGED_EVENT = 'ipe:recharge-cart-changed';
const MAX_CART_QUANTITY = 99;

function resolveStorageKey(userId) {
  const id = userId != null && String(userId).trim() ? String(userId).trim() : 'anonymous';
  return `${STORAGE_PREFIX}:${id}`;
}

function readRaw(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeRaw(key, items) {
  try {
    localStorage.setItem(key, JSON.stringify(items));
  } catch {
    /* ignore quota errors */
  }
  const count = items.reduce((sum, row) => sum + Math.max(1, Number(row.quantity) || 1), 0);
  window.dispatchEvent(new CustomEvent(CART_CHANGED_EVENT, { detail: { count, lines: items.length } }));
}

export function normalizeCartItem(row) {
  if (!row) return row;
  const quantity = Math.min(MAX_CART_QUANTITY, Math.max(1, Number(row.quantity) || 1));
  const unitPriceYuan = Number(row.unitPriceYuan ?? row.priceYuan) || 0;
  return {
    ...row,
    quantity,
    unitPriceYuan,
  };
}

export function buildRechargeCartLineKey(item) {
  const kind = String(item?.kind || '');
  const scope = String(item?.scope || 'personal');
  const ref = kind === 'membership'
    ? String(item?.tier || '')
    : `${String(item?.packGroup || '')}:${String(item?.packId || '')}`;
  const months = Number(item?.durationMonths) || 1;
  return `${kind}|${scope}|${ref}|${months}`;
}

export function getCartItemUnitYuan(item) {
  return Number(item?.unitPriceYuan ?? item?.priceYuan) || 0;
}

export function getCartItemQuantity(item) {
  return Math.max(1, Number(item?.quantity) || 1);
}

export function getCartItemLineYuan(item) {
  return getCartItemUnitYuan(item) * getCartItemQuantity(item);
}

/**
 * @param {object} item
 * @param {string} [userId]
 */
export function addRechargeCartItem(item, userId) {
  const key = resolveStorageKey(userId);
  const lineKey = buildRechargeCartLineKey(item);
  const unitPriceYuan = Number(item.unitPriceYuan ?? item.priceYuan) || 0;
  const items = readRaw(key).map(normalizeCartItem);
  const existingIndex = items.findIndex((row) => row.id === lineKey);
  if (existingIndex >= 0) {
    const existing = items[existingIndex];
    const nextQty = Math.min(MAX_CART_QUANTITY, getCartItemQuantity(existing) + 1);
    items[existingIndex] = normalizeCartItem({
      ...existing,
      ...item,
      id: lineKey,
      unitPriceYuan,
      quantity: nextQty,
      addedAt: Date.now(),
    });
    writeRaw(key, items);
    return items[existingIndex];
  }
  const next = normalizeCartItem({
    ...item,
    id: lineKey,
    unitPriceYuan,
    quantity: 1,
    addedAt: Date.now(),
  });
  writeRaw(key, [next, ...items.filter((row) => row.id !== lineKey)]);
  return next;
}

export function updateRechargeCartItemQuantity(itemId, quantity, userId) {
  const key = resolveStorageKey(userId);
  const qty = Math.min(MAX_CART_QUANTITY, Math.max(1, Number(quantity) || 1));
  const items = readRaw(key).map((row) => {
    if (row.id !== itemId) return normalizeCartItem(row);
    return normalizeCartItem({ ...row, quantity: qty });
  });
  writeRaw(key, items);
}

export function removeRechargeCartItem(itemId, userId) {
  const key = resolveStorageKey(userId);
  const items = readRaw(key).filter((row) => row.id !== itemId);
  writeRaw(key, items);
}

export function removeRechargeCartItems(itemIds, userId) {
  const drop = new Set(itemIds || []);
  if (!drop.size) return;
  const key = resolveStorageKey(userId);
  const items = readRaw(key).filter((row) => !drop.has(row.id));
  writeRaw(key, items);
}

export function clearRechargeCart(userId) {
  writeRaw(resolveStorageKey(userId), []);
}

export function getRechargeCartItems(userId) {
  return readRaw(resolveStorageKey(userId)).map(normalizeCartItem);
}

export function getRechargeCartCount(userId) {
  return getRechargeCartItems(userId).reduce(
    (sum, row) => sum + getCartItemQuantity(row),
    0,
  );
}

export function sumRechargeCartYuan(items) {
  return (items || []).reduce((sum, row) => sum + getCartItemLineYuan(row), 0);
}

export function sumRechargeCartQuantity(items) {
  return (items || []).reduce((sum, row) => sum + getCartItemQuantity(row), 0);
}

export const RECHARGE_CART_CHANGED_EVENT = CART_CHANGED_EVENT;
export const RECHARGE_CART_MAX_QUANTITY = MAX_CART_QUANTITY;
